import { useEffect, useState } from 'react';
import type {
  Placement,
  ReferenceType,
  Strategy,
} from '@floating-ui/react-dom';
import { autoUpdate, flip, size, useFloating } from '@floating-ui/react-dom';

import { themeVars } from '@shieldpay/theme-provider-ui/theme/contract';
import type { Spacing } from '@shieldpay/theme-provider-ui/theme/types';
import { getThemedValue } from '@shieldpay/theme-provider-ui/utilities';

interface UsePositionedPopupOptions {
  placement?: Placement;
  open: boolean;
  maxWidth?: Spacing;
  strategy?: Strategy;
}

type ConstrainStyles = Record<'maxWidth' | 'maxHeight' | 'minWidth', number>;

export const usePositionedPopup = <
  ReferenceElement extends ReferenceType = HTMLButtonElement,
  FloatingElement = HTMLElement,
>({
  placement = 'bottom-start',
  open,
  maxWidth,
  strategy: defaultStrategy = 'absolute',
}: UsePositionedPopupOptions) => {
  const [constrainStyles, setConstrainStyles] =
    useState<Partial<ConstrainStyles>>();
  const [strategy, setStrategy] = useState<Strategy>(defaultStrategy);

  const themeSize = {
    base: getThemedValue(themeVars.spacing.base),
    maxWidth: maxWidth
      ? getThemedValue(themeVars.spacing[maxWidth])
      : undefined,
    maxHeight: getThemedValue(themeVars.dropdown.menu.height),
  };

  const { elements, refs, update, floatingStyles } =
    useFloating<ReferenceElement>({
      open,
      transform: false,
      placement,
      strategy,
      middleware: [
        flip({ padding: themeSize.base }),
        size(
          {
            padding: themeSize.base,
            apply({
              availableWidth,
              availableHeight,
              elements,
              rects: { floating },
            }) {
              if (!constrainStyles) {
                // set initial height. e.g. Without this a long dropdown will not be constrained and will make the page too long
                setConstrainStyles({
                  maxHeight: Math.min(themeSize.maxHeight, availableHeight),
                });
              }

              if (!open) {
                // When we close, reset the strategy to absolute so that we can
                // check if we need to use `fixed` strategy when we open again.
                if (strategy !== defaultStrategy) setStrategy(defaultStrategy);

                // return early if the popup is closed to avoid situations
                // where the popup is constrained to the wrong size
                // e.g. in the table the action menu column is set
                // to overflow hidden when closed.
                return;
              }

              if (
                // If the popup is too big for the available space, switch to fixed strategy
                // which uses the window as the available instead of the reference element.
                strategy !== 'fixed' &&
                (availableHeight < floating.height ||
                  availableWidth < floating.width)
              ) {
                setStrategy('fixed');
                return;
              }

              setConstrainStyles({
                maxWidth: themeSize.maxWidth
                  ? Math.min(themeSize.maxWidth, availableWidth)
                  : Math.min(availableWidth, window.innerWidth * 0.5),
                maxHeight: Math.min(themeSize.maxHeight, availableHeight),
                minWidth: elements.reference.getBoundingClientRect().width,
              });
            },
          },
          [open, strategy],
        ),
      ],
    });

  useEffect(() => {
    if (open && elements.reference && elements.floating) {
      // https://floating-ui.com/docs/autoupdate
      // sets up the event listeners for us

      const cleanup = autoUpdate(elements.reference, elements.floating, update);

      return () => {
        // run the cleanup whenever the popup is shut so we only update when needed
        cleanup();
      };
    }

    update();
  }, [open, elements, update]);

  return {
    getFloating: () => elements.floating as FloatingElement,
    setReference: refs.setReference,
    setFloating: refs.setFloating,
    /**
     * Styles for positioning the popup.
     */
    floatingStyles,
    /**
     * Styles for constraining the dimensions of the popup within the viewport.
     */
    constrainStyles,
  };
};
